import SearhIcon from "../../../assets/searchIcon.svg";
import { addressLookup } from "../../../services/addressLookup";
import {
  type AddressSuggestion,
  useAddressLookupStore,
} from "../../../stores/addressLookupStore";
import {
  useState,
  type ChangeEvent,
  useRef,
  useEffect,
  type KeyboardEvent,
} from "react";

interface AddressLookUpInputProps {
  type: string;
  error?: string;
  placeholder?: string;
  label?: string;
}

export const AddressLookupInput = ({
  type,
  error,
  placeholder = "Search for your home address",
  label = "Address Autofill",
}: AddressLookUpInputProps) => {
  const {
    setSelectedAddress,
    setAddressLookupModalOpen,
    setSuggestions,
    selectedAddresses,
    suggestions,
    getFormattedAddress,
  } = useAddressLookupStore();
  const [inputValue, setInputValue] = useState(selectedAddresses[type] || "");
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const filterSuggestions = (
    items: AddressSuggestion[]
  ): AddressSuggestion[] => {
    const bestItems = new Map<string, AddressSuggestion>();
    for (const item of items) {
      const suggestion = item;
      const existingItem = bestItems.get(suggestion.Text);
      if (existingItem) {
        if (
          existingItem.Type !== "BuildingNumber" ||
          suggestion.Type === "BuildingNumber"
        ) {
          bestItems.set(suggestion.Text, suggestion);
        }
      } else {
        bestItems.set(suggestion.Text, suggestion);
      }
    }
    return Array.from(bestItems.values());
  };

  const fetchSuggestions = async (input: string) => {
    try {
      const response: AddressSuggestion[] = await addressLookup(input);
      const filteredSuggestions = filterSuggestions(response);
      setSuggestions(type, filteredSuggestions);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions(type, []);
    }
  };

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setInputValue(input);
    setSelectedAddress(type, input);
    if (input.length > 2) {
      await fetchSuggestions(input);
    } else {
      setSuggestions(type, []);
    }
  };

  const formatSuggestionText = (suggestion: AddressSuggestion): string => {
    let suggestionText = suggestion.Text;
    if (suggestion.Type === "Address" || suggestion.Type === "BuildingNumber") {
      const descriptionParts = suggestion.Description?.split(", ") || [];
      const shortDescription = descriptionParts.slice(0, 2).join(", ");
      suggestionText = `${suggestionText}, ${shortDescription}`;
      if (suggestion.UnitNumber) {
        suggestionText += `, Unit ${suggestion.UnitNumber}`;
      }
    }
    return suggestionText;
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    setSelectedAddress(type, suggestion.Text);
    if (suggestion.Type === "BuildingNumber") {
      setSelectedAddress(type, suggestion.Text);
      setAddressLookupModalOpen(type, true);
    } else if (suggestion.Type === "Address") {
      setSelectedAddress(type, suggestion.Text);
      getFormattedAddress(type, suggestion.Id);
      setSuggestions(type, []);
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLLIElement>,
    suggestion: AddressSuggestion
  ) => {
    if (e.key === "Enter") {
      handleSuggestionClick(suggestion);
    }
  };

  useEffect(() => {
    setInputValue(selectedAddresses[type] || "");
  }, [selectedAddresses, type]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSuggestions(type, []);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [type, setSuggestions]);

  return (
    <div
      ref={dropdownRef}
      className="flex flex-col space-y-2 items-center w-full relative"
    >
      {label && (
        <label className="block text-sm font-medium text-gray-900 w-full">
          {label}
        </label>
      )}
      <div className="flex flex-col items-center w-full gap-4 relative">
        <img
          src={SearhIcon}
          alt=""
          className="absolute left-4 translate-y-1/2 mt-1.5"
        />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`pl-10 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-[8px] focus:ring-z-dark-moss-green focus:border-z-dark-moss-green block w-full p-2.5 ${
            error ? "border-red-600" : ""
          } rounded-md`}
        />
        {suggestions[type]?.length > 0 && (
          <div className="absolute top-full left-0 border-2 border-[#F1F1F1] right-0 z-10 mt-2 max-h-[150px] overflow-y-auto bg-white shadow-lg rounded-[8px]">
            <ul>
              {suggestions[type].map((suggestion, i) => (
                <li
                  key={suggestion.Id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onKeyDown={(e) => handleKeyDown(e, suggestion)}
                  className={`cursor-pointer ${
                    suggestions[type].length === i + 1
                      ? ""
                      : "border-b-2 border-b-[#F1F1F1]"
                  }  px-4 py-2`}
                >
                  {formatSuggestionText(suggestion)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {error && (
        <span className="text-red-600 text-sm text-left w-full">{error}</span>
      )}
    </div>
  );
};
